// Code generated by apic; DO NOT EDIT.
import { createContext, useContext } from "react";
import type { ApicClient } from "./client";

// --- TSC-11: ambient in-memory bearer-token holder ---
// A single module-level mutable token with one writer (setApiToken). Pair it
// with createClient({ bearerToken: () => getApiToken() }) so every request reads
// the live token. The sole writer is setApiToken — call it on login, sign-out,
// and (critically) on REHYDRATE BEFORE FIRST RENDER: read the persisted token
// from storage and setApiToken(it) before the first createClient()/render so the
// app's first request is already authenticated. This holder is intentionally
// process-global (a module singleton); it is not React state and does not
// trigger re-renders.
let ambientToken: string | null = null;

/** Returns the current ambient bearer token, or null if none is set. */
export function getApiToken(): string | null {
  return ambientToken;
}

/** Sets (or clears, with null) the ambient bearer token. Sole writer. */
export function setApiToken(token: string | null): void {
  ambientToken = token;
}

// --- TSC-11: React Context provider + useClient() ---
// Wrap the tree once in <ApicProvider value={client}> (client from
// createClient(...) / useApicClient(...)) and call const client = useClient()
// inside components, instead of threading the client into every hook call:
//   const client = useClient();
//   const posts = useListPosts(client, req);
// The per-route use{X}(client, ...) hook signatures are unchanged (additive).
const ApicClientContext = createContext<ApicClient | null>(null);

/**
 * Returns the ApicClient React context. Exposed so a consumer can build a
 * custom Provider or read the context directly; most apps use ApicProvider +
 * useClient() and never touch this.
 */
export function createApicContext() {
  return ApicClientContext;
}

/** Provider component: <ApicProvider value={client}>…</ApicProvider>. */
export const ApicProvider = ApicClientContext.Provider;

/**
 * Returns the ApicClient provided by the nearest ApicProvider. Throws if no
 * provider is mounted above (a programming error — the tree must be wrapped).
 */
export function useClient(): ApicClient {
  const client = useContext(ApicClientContext);
  if (client === null) {
    throw new Error("useClient() must be used within a <ApicProvider>");
  }
  return client;
}

// --- TSC-12: UNVERIFIED JWT-claims decoder (UI-gating only) ---
// SECURITY: decodeJwtPayload does NOT verify the JWT signature. The decoded
// claims are attacker-controllable and MUST NOT be used as a security or
// authorization boundary — they are for UI affordance gating only (show/hide a
// menu item). The SERVER remains the sole authority on auth/authz. The decode is
// runtime-agnostic (no atob): it reuses the client's RN-safe base64 alphabet so
// it imports and runs on web, Electron, and React Native (Hermes). TSC-12/16.
export type JwtClaims = {
  sub?: string;
  roles?: string[];
  exp?: number;
  [claim: string]: unknown;
};

const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const BASE64_LOOKUP: Record<string, number> = (() => {
  const table: Record<string, number> = {};
  for (let i = 0; i < BASE64_ALPHABET.length; i++) {
    table[BASE64_ALPHABET[i] as string] = i;
  }
  return table;
})();

// decodeBase64 decodes a STANDARD base64 string (after base64url has been
// normalized to base64) to bytes, with no atob. Non-alphabet chars (incl. "=")
// are ignored; missing trailing padding is tolerated.
function decodeBase64(b64: string): Uint8Array {
  const clean = b64.replace(/[^A-Za-z0-9+/]/g, "");
  const out: number[] = [];
  for (let i = 0; i < clean.length; i += 4) {
    const c0 = BASE64_LOOKUP[clean[i] as string] ?? 0;
    const c1 = BASE64_LOOKUP[clean[i + 1] as string] ?? 0;
    const c2 = clean[i + 2] === undefined ? -1 : (BASE64_LOOKUP[clean[i + 2] as string] ?? 0);
    const c3 = clean[i + 3] === undefined ? -1 : (BASE64_LOOKUP[clean[i + 3] as string] ?? 0);
    out.push((c0 << 2) | (c1 >> 4));
    if (c2 >= 0) {
      out.push(((c1 & 15) << 4) | (c2 >> 2));
    }
    if (c3 >= 0) {
      out.push(((c2 & 3) << 6) | c3);
    }
  }
  return Uint8Array.from(out);
}

// utf8Decode decodes UTF-8 bytes to a string, preferring TextDecoder when
// present and falling back to a portable decoder (keeps RN/Hermes-safe).
function utf8Decode(bytes: Uint8Array): string {
  if (typeof TextDecoder !== "undefined") {
    return new TextDecoder().decode(bytes);
  }
  let out = "";
  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i++] as number;
    if (b < 0x80) {
      out += String.fromCharCode(b);
    } else if (b < 0xe0) {
      out += String.fromCharCode(((b & 0x1f) << 6) | ((bytes[i++] as number) & 0x3f));
    } else if (b < 0xf0) {
      out += String.fromCharCode(
        ((b & 0x0f) << 12) | (((bytes[i++] as number) & 0x3f) << 6) | ((bytes[i++] as number) & 0x3f),
      );
    } else {
      const cp =
        ((b & 0x07) << 18) |
        (((bytes[i++] as number) & 0x3f) << 12) |
        (((bytes[i++] as number) & 0x3f) << 6) |
        ((bytes[i++] as number) & 0x3f);
      const off = cp - 0x10000;
      out += String.fromCharCode(0xd800 + (off >> 10), 0xdc00 + (off & 0x3ff));
    }
  }
  return out;
}

/**
 * Decodes the (UNVERIFIED) payload of a JWT into its claims, or returns null if
 * the token is not a well-formed JWT with a JSON-object payload. NEVER throws.
 * SECURITY: signature is NOT checked — UI-gating only (see file header).
 *
 * @security UNVERIFIED — this does NOT verify the JWT signature. The decoded
 * claims are attacker-controllable and MUST NOT gate authorization, data
 * access, or any security decision. UI affordance gating only (show/hide a
 * menu item); the SERVER is the sole authority on authn/authz.
 */
export function decodeJwtPayload(token: string): JwtClaims | null {
  if (typeof token !== "string") {
    return null;
  }
  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }
  const segment = parts[1] as string;
  // base64url -> base64.
  const b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  try {
    const json = utf8Decode(decodeBase64(b64));
    const parsed = JSON.parse(json) as unknown;
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * Reports whether the (UNVERIFIED) claims include the given role. UI-gating only.
 *
 * @security UNVERIFIED — operates on unverified, attacker-controllable claims
 * (see decodeJwtPayload). MUST NOT be used as a security or authorization
 * boundary; the SERVER enforces RBAC. UI affordance gating only.
 */
export function hasRole(claims: JwtClaims | null, role: string): boolean {
  if (!claims) {
    return false;
  }
  const roles = claims.roles;
  return Array.isArray(roles) && roles.includes(role);
}
